import { useState } from "react";
import { Icon } from "../components/Icons";
import { actions, STUDIO } from "../lib/store";
import { runAgent } from "../lib/ai";
import { projectSnapshot } from "../lib/snapshot";
import { relative } from "../lib/format";
import type { Project } from "../lib/types";

/* The daily read: the agent goes through the whole project and prepares the
   actions a principal would otherwise assemble by hand — with the chase emails
   already drafted. It prepares; you decide. Nothing is ever sent automatically. */
const HUE: Record<string, string> = { high: "var(--danger)", medium: "var(--warn)", low: "var(--ink-3)" };
const KIND_ICON: Record<string, keyof typeof Icon> = { email: "send", chase: "bell", flag: "spark", task: "check" };

export function AgentPanel({ p, canEdit }: { p: Project; canEdit: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = p.agentRun;

  async function go() {
    setBusy(true); setErr(null);
    const res = await runAgent(projectSnapshot(p), STUDIO.name, p.client?.name ?? "the client");
    setBusy(false);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The agent isn't switched on (needs an AI key)." : "Couldn't reach the agent."); return; }
    actions.setAgentRun(p.id, {
      ranOn: new Date().toISOString(), summary: res.summary ?? "",
      actions: (res.actions ?? []).map((a) => ({ kind: a.kind, title: a.title, detail: a.detail, to: a.to ?? undefined, draft: a.draft ?? undefined, urgency: a.urgency })),
    });
  }

  const open = (run?.actions ?? []).filter((a) => !a.done);
  return (
    <div className="card reveal" style={{ overflow: "hidden", marginBottom: 22, borderLeft: "3px solid var(--accent)" }}>
      <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: run || err ? "1px solid var(--line)" : "none", gap: 10 }}>
        <div className="row" style={{ gap: 8 }}>
          <Icon.spark width={16} style={{ color: "var(--accent-ink)" }} />
          <span className="kicker">Today on this project</span>
          {open.length > 0 && <span className="badge accent" style={{ fontSize: 9 }}>{open.length} to action</span>}
          {run && <span className="dim" style={{ fontSize: 11.5 }}>read {relative(run.ranOn)}</span>}
        </div>
        {canEdit && <button className="btn accent sm" disabled={busy} onClick={go}><Icon.spark width={13} />{busy ? "Reading…" : run ? "Re-run" : "Run the agent"}</button>}
      </div>

      {err && <div style={{ padding: "12px 18px", color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
      {busy && <div className="row" style={{ padding: "14px 18px", gap: 8, color: "var(--ink-3)", fontSize: 13 }}><span className="dot" />Reading the drawings, billing, reviews and RFIs…</div>}

      {!run && !busy && !err && (
        <div className="dim" style={{ padding: "16px 18px", fontSize: 13, lineHeight: 1.6 }}>
          The agent reads everything on the project — money, drawings, reviews, RFIs, variations — and prepares the day's actions with the chase emails already written. It drafts; you send.
        </div>
      )}

      {run && !busy && (
        <>
          {run.summary && <div style={{ padding: "13px 18px", fontSize: 13.5, lineHeight: 1.65, borderBottom: run.actions.length ? "1px solid var(--line)" : "none" }}>{run.summary}</div>}
          {run.actions.length === 0 && <div className="dim" style={{ padding: "14px 18px", fontSize: 13 }}>Nothing needs chasing — the project is in good order.</div>}
          {run.actions.map((a, i) => (
            <AgentRow key={i} p={p} a={a} i={i} canEdit={canEdit} />
          ))}
        </>
      )}
    </div>
  );
}

function AgentRow({ p, a, i, canEdit }: { p: Project; a: any; i: number; canEdit: boolean }) {
  const [showDraft, setShowDraft] = useState(false);
  const IconCmp = Icon[KIND_ICON[a.kind] ?? "check"] as any;
  const mailto = a.draft
    ? `mailto:${a.to && a.to.includes("@") ? a.to : p.client?.email ?? ""}?subject=${encodeURIComponent(`${p.name} — ${a.title}`)}&body=${encodeURIComponent(a.draft)}`
    : "";
  return (
    <div style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none", opacity: a.done ? 0.55 : 1 }}>
      <div className="row between wrap" style={{ gap: 10 }}>
        <span className="row" style={{ gap: 9, minWidth: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: 7, background: HUE[a.urgency], flexShrink: 0 }} />
          <IconCmp width={13} style={{ color: "var(--ink-3)", flexShrink: 0 }} />
          <span style={{ fontSize: 14, fontWeight: 600, textDecoration: a.done ? "line-through" : "none" }}>{a.title}</span>
        </span>
        <span className="row" style={{ gap: 8, flexShrink: 0 }}>
          {a.to && <span className="badge" style={{ fontSize: 8.5 }}>{a.to}</span>}
          <span className="badge" style={{ fontSize: 8.5, color: HUE[a.urgency] }}>{a.urgency}</span>
        </span>
      </div>
      <div className="dim" style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.55 }}>{a.detail}</div>
      {showDraft && a.draft && (
        <div className="panel" style={{ padding: "11px 13px", marginTop: 9, whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.6 }}>{a.draft}</div>
      )}
      {canEdit && (
        <div className="row wrap" style={{ gap: 8, marginTop: 9 }}>
          {a.draft && <button className="chip" style={{ fontSize: 11 }} onClick={() => setShowDraft((v) => !v)}>{showDraft ? "Hide draft" : "Read the draft"}</button>}
          {a.draft && <a className="chip" style={{ fontSize: 11 }} href={mailto}>Send from my email</a>}
          <span style={{ flex: 1 }} />
          <button className="chip" style={{ fontSize: 11 }} onClick={() => actions.setAgentActionDone(p.id, i, !a.done)}>{a.done ? "Reopen" : "Done"}</button>
        </div>
      )}
    </div>
  );
}
